import React, {useCallback, useEffect, useState} from "react"
import { Button, Drawer, Accordion } from 'components/common';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MdOutlineClose } from "react-icons/md";
import { FaFileSignature, FaBuilding } from "react-icons/fa";
import { MdOutlinePayments } from "react-icons/md";
import { RiMailSendLine } from "react-icons/ri";
import PropTypes from 'prop-types';
import { RiSecurePaymentFill } from "react-icons/ri";
import { getPaymentSchedules } from "redux/crm/project/paymentScheduleSlice";
import { formatDate } from "helpers/utils";
import { formatThousands } from "helpers/number";

// View only. Payments are recorded, edited and released in ONE place — the job
// building's Payment tab in Contract Management (also before the contract exists).
const PaymentTable = ({ paymentData, releaseInfo }) => (
  <div className="tw-overflow-x-auto">
    <table className="tw-w-full tw-mb-0 tw-text-sm tw-border tw-border-solid tw-border-slate-200">
      <thead className="tw-bg-slate-50 tw-font-bold">
        <tr className="tw-border-b tw-border-solid tw-border-slate-200">
          <th scope="col" className="tw-text-left tw-py-2 tw-px-2">Trans Date</th>
          <th scope="col" className="tw-text-left tw-py-2 tw-px-2">Name</th>
          <th scope="col" className="tw-text-left tw-py-2 tw-px-2">Plan Date</th>
          <th scope="col" className="tw-text-left tw-py-2 tw-px-2">Actual Date</th>
          <th scope="col" className="tw-text-center tw-py-2 tw-px-2">Received (%)</th>
          <th scope="col" className="tw-text-right tw-py-2 tw-px-2">Amount Received</th>
          <th scope="col" className="tw-text-center tw-py-2 tw-px-2">Currency</th>
          <th scope="col" className="tw-text-left tw-py-2 tw-px-2">Remark</th>
          <th scope="col" className="tw-text-center tw-py-2 tw-px-2">Release</th>
        </tr>
      </thead>
      <tbody>
        {paymentData && paymentData.length > 0 ? (
          paymentData.map(item => {
            const released = releaseInfo?.[String(item.id)];
            return (
              <tr key={item.id} className="odd:tw-bg-white even:tw-bg-slate-50 tw-border-b tw-border-solid tw-border-slate-100 tw-align-middle">
                <td className="tw-py-2 tw-px-2">{formatDate(item.trans_date)}</td>
                <td className="tw-py-2 tw-px-2 tw-font-semibold">{item.name}</td>
                <td className="tw-py-2 tw-px-2">{formatDate(item.plan_date)}</td>
                <td className="tw-py-2 tw-px-2">{formatDate(item.actual_date)}</td>
                <td className="tw-py-2 tw-px-2 tw-text-center">
                  <span className="tw-text-blue-600">
                    {item?.receive_percent > 0 ? `${parseFloat(item?.receive_percent)?.toFixed(0)}%` : ''}
                  </span>
                </td>
                <td className="tw-py-2 tw-px-2 tw-text-right">
                  {Number(item.amount_received) > 0 ? formatThousands(item.amount_received) : ''}
                </td>
                <td className="tw-py-2 tw-px-2 tw-text-center tw-text-slate-500">{item.currency}</td>
                <td className="tw-py-2 tw-px-2">{item.remark}</td>
                <td className="tw-py-2 tw-px-2 tw-text-center">
                  {released ? (
                    <span title={`Released${released.sent_at ? ` on ${formatDate(released.sent_at)}` : ''}${released.sent_by ? ` by ${released.sent_by}` : ''}`}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-700 tw-text-[11px] tw-font-semibold">
                      <RiMailSendLine size={10} /> Released
                    </span>
                  ) : (
                    <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-500 tw-text-[11px] tw-font-semibold">
                      Not released
                    </span>
                  )}
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={9} className="tw-text-center tw-text-slate-500 tw-py-4">
              No payment data available.
            </td>
          </tr>
        )}
      </tbody>

      {paymentData && paymentData.length > 0 && (
        <tfoot>
          <tr>
            <td colSpan={4} className="tw-text-right tw-text-blue-600 tw-font-semibold tw-py-2 tw-px-2">Total:</td>
            <td className="tw-text-center tw-py-2 tw-px-2">
              <span className="tw-text-blue-600">
                {Number(paymentData.reduce((acc, item) => acc + (Number(item.receive_percent) || 0), 0)).toFixed(0)}%
              </span>
            </td>
            <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold">
              {formatThousands(paymentData.reduce((acc, item) => acc + (Number(item.amount_received) || 0), 0))}
            </td>
            <td colSpan={3}></td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

PaymentTable.propTypes = {
  paymentData: PropTypes.array.isRequired,
  releaseInfo: PropTypes.object,
};

const ProjectSchedulePaymentModal = ({open, setOpen, projectId}) => {
  // Payment plans (also those without a contract yet) — view only.
  const { data } = useSelector((state) => state.crm_project_payment_schedule);
  const [activeKey, setActiveKey] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      setActiveKey(data.map((_, idx) => idx.toString()));
    }
  }, [data]);

  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    try {
      await dispatch(getPaymentSchedules(projectId)).unwrap();
    }
    catch (error) {
      toast.error(error.message);
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, fetchData]);

  const closeModal = () => {
    setOpen(false);
  }

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  return (
    <Drawer
      show={open}
      onHide={closeModal}
      placement="right"
      backdropClick={false}
      width="60%"
      className="tw-text-sm"
      title={<><RiSecurePaymentFill /> Payment plan</>}
      footer={(
        <Button size="sm" variant="danger" onClick={closeModal}><MdOutlineClose /> Close</Button>
      )}
    >
      <div className="tw-mb-1">You have <strong>{data.length || 0} payment plan{data.length === 1 ? '' : 's'}</strong> for this project</div>
      <div className="tw-mb-2 tw-text-xs tw-text-slate-500">
        View only — payments are recorded and released in Execution › Finance Security.
      </div>

      <Accordion
        className="tw-text-sm tw-mt-3"
        activeKeys={activeKey.length ? activeKey : ["0"]}
        onChange={handleAccordionSelect}
      >
        {data && data.map((item, index) => (
          <Accordion.Item
            key={item.id}
            eventKey={index.toString()}
            header={(
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-gap-3 tw-w-full tw-text-slate-900 tw-font-normal">
                {item.pre_contract ? (
                  <div>
                    <MdOutlinePayments className="tw-text-blue-600" />{' '}
                    <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-[11px] tw-font-bold tw-bg-amber-100 tw-text-amber-700">No contract yet</span>
                  </div>
                ) : (
                  <>
                    <div><MdOutlinePayments className="tw-text-blue-600" /> <span className="tw-text-blue-600">{item.contract?.contract_number}</span></div>
                    <div><FaFileSignature className="tw-text-yellow-500" /> Contract date: <span className="tw-text-blue-600">{formatDate(item.contract?.contract_date)}</span></div>
                  </>
                )}
                <div><FaBuilding className="tw-text-blue-600" /> Building: <span className="tw-text-blue-600">{item.name}</span></div>
              </div>
            )}
          >
            <div className="tw-m-2 tw-p-2">
              <PaymentTable
                paymentData={item.payment_plans || []}
                releaseInfo={item.milestone_release_info} />
            </div>
          </Accordion.Item>
        ))}
      </Accordion>
    </Drawer>
  )
};

ProjectSchedulePaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  title: PropTypes.string,
  projectId: PropTypes.number
};

export default ProjectSchedulePaymentModal;
